import { createDefu } from 'defu'
import type { AxiosResponse } from 'axios'

export const useCompaniesStore = defineStore('companies', () => {
  const { toggleModal } = useOverlays()
  const api = useApi()

  // Create custom defu that replaces arrays instead of merging them
  const merger = createDefu((obj, key, value) => {
    if (Array.isArray(obj[key]) && Array.isArray(value)) {
      (obj as any)[key] = Array.from(new Set([...obj[key], ...value])).filter(Boolean)

      return true
    }
  })

  const segmentationSortFieldsToCompaniesSortFields = (sortFields?: SegmentationSortFields) => {
    if (!sortFields || Object.keys(sortFields).length === 0) {
      return undefined
    }

    return Object.entries(sortFields).map(
      ([key, order]) => ({
        key: key as CompaniesSortFields[number]['key'],
        order,
      }),
    )
  }

  const fetchCompanies = async (params: {
    actionId?: number
    domainsToExclude?: string[]
    listId?: number
    page?: number
    query?: SegmentationCondition[]
    search?: string
    searchFields?: SegmentationAttributeWeCanSearch[]
    similarDomains?: string[]
    simplified?: boolean
    size?: number
    sortFields?: SegmentationSortFields
    sortKey?: CompaniesSortKey
    sortOrder?: CompaniesSortOrder
  }) => {
    try {
      let response: AxiosResponse

      const sortFields = segmentationSortFieldsToCompaniesSortFields(params.sortFields)

      // Fetch companies for a specific list
      if (params.listId) {
        response = await api.lists.companies.fetch(
          params.listId,
          {
            actionId: params.actionId,
            page: params.page,
            query: params.query,
            simplified: params.simplified,
            size: params.size,
            sortFields,
            sortKey: params.sortKey,
            sortOrder: params.sortOrder,
          },
        ) as AxiosResponse
      }
      // Fetch similar companies for a list of domains
      else if (params.similarDomains) {
        response = await api.companies.fetchSimilar({
          domains: params.similarDomains,
          page: params.page,
          simplified: params.simplified,
          size: params.size,
        }) as AxiosResponse
      }
      // Search companies from query
      else {
        response = await api.companies.fetch({
          actionId: params.actionId,
          domainsToExclude: (params.domainsToExclude || []).join(','),
          page: params.page,
          query: params.query,
          search: params.search,
          searchFields: params.searchFields,
          simplified: params.simplified,
          size: params.size,
          sortFields,
          sortKey: params.sortKey,
          sortOrder: params.sortOrder,
        }) as AxiosResponse
      }

      // Display popup when maxScrollResultsReached is true
      if (response.data.meta.maxScrollResultsReached) {
        return toggleModal({
          data: { totalCompanies: response.data.meta.total },
          opened: true,
          title: 'maxScrollResultsReached',
          type: 'subscription',
        })
      }

      // Extract data from response
      const companies = response.data.companies
      const lastPage = response.data.meta.lastPage
      const nextPage = params.page && params.page >= response.data.meta.lastPage ? -1 : (params.page || 1) + 1
      const perPage = response.data.meta.perPage
      const freeRequest = response?.data?.meta?.freeRequest
      const total = response?.data?.meta?.total

      // Track whether we're filtering results (via query, search, list, similar domains, or search fields)
      const hasQuery = !!(
        params.query?.length
        || params.actionId
        || params.search
        || params.similarDomains?.length
        || params.searchFields?.length
      )

      return {
        companies,
        freeRequest,
        hasQuery,
        lastPage,
        nextPage,
        perPage,
        total,
      }
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const fetchCompany = async (params: {
    domain: string
    listId?: number
  }) => {
    try {
      let companyFetched: CompanyV2 | undefined

      // Fetch companies for a specific list
      if (params.listId) {
        const { data } = await api.lists.companies.fetchOne(params.listId, params.domain)

        companyFetched = data
      }
      // Fetch company by domain
      else {
        const { data } = await api.companies.fetchOne(params.domain)

        companyFetched = data
      }

      return companyFetched
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const fetchCompanyContext = async (params: {
    domain: string
  }) => {
    try {
      const { data } = await api.companies.fetchContext(params.domain)

      return data
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const fetchCount = async (params: {
    actionId?: number
    query?: SegmentationCondition[]
    search?: string
    setTotal?: boolean
  }) => {
    try {
      const { data: { count: countFetched } } = await api.companies.count({
        actionId: params.actionId,
        query: params.query,
        search: params.search,
      })

      return countFetched
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const fetchByName = async (params: {
    name: string
    page?: number
    searchFields?: SegmentationAttributeWeCanSearch[]
    simplified?: boolean
    size?: number
  }) => {
    const { data } = await api.companies.fetchByName({
      name: params.name,
      page: params.page,
      simplified: params.simplified,
      size: params.size,
    })

    return data
  }

  const fetchByPrompt = async (params: {
    page?: number
    prompt: string
    similarity?: number
    simplified?: boolean
    size?: number
    sortFields?: SegmentationSortFields
    sortKey?: CompaniesSortKey
    sortOrder?: CompaniesSortOrder
  }) => {
    const { data } = await api.companies.fetchByPrompt({
      page: params.page,
      prompt: params.prompt,
      similarity: params.similarity,
      simplified: params.simplified,
      size: params.size,
      sortFields: segmentationSortFieldsToCompaniesSortFields(params.sortFields),
      sortKey: params.sortKey,
      sortOrder: params.sortOrder,
    })

    return data
  }

  // Setters
  const removeListIdFromCompanies = (params: {
    companies: CompanyV2[]
    listId: number
  }) => {
    for (const company of params.companies) {
      if (company.meta?.listIds?.includes(params.listId)) {
        const index = company.meta.listIds.indexOf(params.listId)

        if (index !== -1) {
          company.meta.listIds.splice(index, 1)
        }
      }
    }
  }

  const updateListIdsForCompanies = (params: {
    action: 'attach' | 'detach'
    companiesIds: number[]
    company?: CompanyV2
    currentCompanies: CompanyV2[]
    listId: number
  }) => {
    // Create a deep clone of the companies array
    return JSON.parse(JSON.stringify(params?.currentCompanies || [])).map((company: CompanyV2) => {
      // Check if this company is in the list of companies to update
      if (company?.meta?.mysqlId && params.companiesIds.includes(company.meta.mysqlId)) {
        // Handle attach action
        if (params.action === 'attach' && !company.meta?.listIds?.includes(params.listId)) {
          company.meta.listIds = Array.from(new Set([...(company?.meta?.listIds || []), params.listId]))
        }
        // Handle detach action
        else if (params.action === 'detach' && company.meta?.listIds?.includes(params.listId)) {
          const index = company.meta.listIds?.indexOf(params.listId)

          if (index !== -1 && typeof index === 'number') {
            company.meta.listIds = company.meta.listIds.filter(id => id !== params.listId)
          }
        }
      }

      return company
    })
  }

  const updateCompany = async (params: {
    company: CompanyV2
    currentCompanies: CompanyV2[]
  }) => {
    const isCompanyInCurrentPage = params.currentCompanies.find(company => company.domain === params.company.domain)

    if (isCompanyInCurrentPage) {
      return params.currentCompanies.map((company) => {
        if (company.domain === params.company.domain) {
          return params.company
        }

        return company
      })
    }

    return params.currentCompanies
  }

  const addOrReplaceCompanies = async (params: {
    actionId?: number
    currentCompanies: CompanyV2[]
    newCompanies: CompanyV2[]
    onlyReplace?: boolean
    perPage: number
  }) => {
    const newCompanies = [...params.currentCompanies]

    for (const company of params.newCompanies) {
      const existingCompany = newCompanies.findIndex(companyInList => companyInList.domain?.domain === company?.domain?.domain)

      if (existingCompany !== -1) {
        // Merge company new company with existing company
        newCompanies[existingCompany] = merger(company, newCompanies[existingCompany])
      }
      else if (!params.onlyReplace) {
        newCompanies.unshift(company)
      }
    }

    return newCompanies.slice(0, params.perPage)
  }

  const removeCompanies = async (params: {
    existingCompanies: CompanyV2[]
    mysqlIds: number[]
  }) => {
    return [...params.existingCompanies].filter(company => !params.mysqlIds.includes(company.meta!.mysqlId!))
  }

  return {
    addOrReplaceCompanies,
    fetchByName,
    fetchByPrompt,
    fetchCompanies,
    fetchCompany,
    fetchCompanyContext,
    fetchCount,
    removeCompanies,
    removeListIdFromCompanies,
    updateCompany,
    updateListIdsForCompanies,
  }
})
